import { formatDisplayDate, formatDisplayDateTime, parseDate } from "utils/date-helper"
import { rootStore } from "stores/root-store"
import AutocompleteEditInputCell from "components/common/autocomplete-edit-input-cell"
import DateTimeEditInputCell from "components/common/datetime-edit-input-cell"
import { inboundDeliveryBusiness } from "businesses/inbound-delivery-business"
import StatusComponent from "components/common/status-component"
import { GlobalData } from "shared/global-data"
import { CommonHyperlink } from "components/common-styles"

export const filterData = [
  {
    name: "before_date",
    text: "Before Date",
    checked: true,
  },
  {
    name: "after_date",
    text: "After Date",
    checked: true,
  },
  {
    name: "warehouse",
    text: "Warehouse",
  },
  {
    name: "receipt_type",
    text: "Receipt Type",
  },
  {
    name: "receipt_status",
    text: "Receipt Status",
  },
]

export const hiddenColumns = {
  CLOSE_DATE: false,
  LAST_UPDATE_DATETIME: false,
  CREATION_DATE: false,
  TOTAL_LINES: false,
  TOTAL_WEIGHT: false,
  TOTAL_VOLUME: false,
}

export const columns = [
  {
    field: "RECEIPT_ID",
    headerName: "Receipt ID",
    flex: 1,
    minWidth: 200,
    renderCell: params => <CommonHyperlink to={`/inbound-delivery/${encodeURIComponent(params.value)}/${encodeURIComponent(params.row["ID"])}`}>{params.value}</CommonHyperlink>,
  },
  {
    field: "WAREHOUSE",
    headerName: "Warehouse",
  },

  {
    field: "RECEIPT_TYPE",
    headerName: "Receipt Type",
    width: 180,
  },
  {
    field: "RECEIPT_STATUS",
    headerName: "Receipt Status",
    width: 150,
    renderCell: params => <StatusComponent status={params.value} />,
  },
  {
    field: "TRAILER_ID",
    headerName: "Tracking Number",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "ERP_ORDER",
    headerName: "ERP Number",
    width: 150,
  },
  {
    field: "TOTAL_QUANTITY",
    headerName: "Total Qty",
    type: "number",
  },
  {
    field: "TOTAL_VOLUME",
    headerName: "Total Volume (CBM)",
    type: "number",
  },
  {
    field: "TOTAL_WEIGHT",
    headerName: "Total Weight (KG)",
    type: "number",
  },
  {
    field: "TOTAL_LINES",
    headerName: "Total Lines",
    type: "number",
  },
  {
    field: "CREATION_DATE",
    headerName: "Creation Date",
    width: 150,
    renderCell: params => {
      return formatDisplayDateTime(params.value)
    },
  },
  {
    field: "SCHEDULED_DATE",
    headerName: "Scheduled Date",
    width: 120,
    renderCell: params => {
      return formatDisplayDate(params.value)
    },
  },
  {
    field: "LAST_UPDATE_DATETIME",
    headerName: "Last Update Date",
    width: 150,
    renderCell: params => {
      return formatDisplayDateTime(params.value)
    },
  },
  {
    field: "SOURCE",
    headerName: "Supplier",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "CLOSE_DATE",
    headerName: "Close Date",
    width: 150,
    renderCell: params => {
      return formatDisplayDateTime(params.value)
    },
  },
  {
    field: "RECEIPT_VARIANCE",
    headerName: "Receipt Variance",
    type: "number",
  },
]

export const columnsUploadFunctionsMap = {
  warehouse: {
    renderEditCell: params => {
      return <AutocompleteEditInputCell {...params} options={rootStore.getOptionsByFilter("WAREHOUSE").map(option => ({ label: option.label, id: option.value }))} />
    },
    customValidation: (rowId, val, allData) => {
      const found = rootStore.getOptionsByFilter("WAREHOUSE").find(x => x.value == val)
      if (!found) return `${val} is unavailable.`

      return ""
    },
    serverSideValidation: async (rowId, val, allData) => {
      const row = allData.find(x => rowId == x.id)
      const res = await inboundDeliveryBusiness.getData({ search_term: row["receipt_id"], warehouse: val })
      if (res && res.length > 0) return `${row["receipt_id"]} already exists in warehouse ${val}.`

      return ""
    },
  },
  receipt_id: {
    customValidation: (rowId, val, allData) => {
      const curRowData = allData.find(x => rowId == x.id)
      const found = allData.find(x => x["receipt_id"] == val && rowId != x.id && x["item"] == curRowData["item"])
      if (found) return `Receipt ${val} with SKU ${curRowData["item"]} already exists.`

      return ""
    },
    serverSideValidation: async (rowId, val, allData) => {
      const row = allData.find(x => rowId == x.id)
      const res = await inboundDeliveryBusiness.getData({ search_term: val, warehouse: row["warehouse"] })
      if (res && res.length > 0) return `Receipt ${val} already exists in warehouse ${row["warehouse"]}.`

      return ""
    },
  },
  receipt_type: {
    renderEditCell: params => {
      return <AutocompleteEditInputCell {...params} options={rootStore.getOptionsByFilter("RECEIPT_TYPE").map(option => ({ label: option.label, id: option.value }))} />
    },
    customValidation: (rowId, val, allData) => {
      const found = rootStore.getOptionsByFilter("RECEIPT_TYPE").find(x => x.value == val)
      if (!found) return `${val} is unavailable.`

      return ""
    },
  },
  item: {
    renderEditCell: params => {
      return <AutocompleteEditInputCell {...params} options={GlobalData.ProductListSelection} />
    },
    customValidation: (rowId, productCode, allData) => {
      productCode = (productCode || "").toUpperCase()
      const found = GlobalData.ProductListSelection.find(x => x.id.toUpperCase() == productCode)
      if (!found) return `Invalid Product Code`

      return ""
    },
  },
  scheduled_date: {
    valueGetter: params => {
      if (!params) {
        return null
      }

      if (params.value) {
        return typeof params.value == "string" ? parseDate(params.value) : params.value
      }

      return typeof params == "string" ? parseDate(params) : params
    },
    renderEditCell: params => {
      return <DateTimeEditInputCell {...params} />
    },
  },
  receipt_date: {
    valueGetter: params => {
      if (!params) {
        return null
      }

      if (params.value) {
        return typeof params.value == "string" ? parseDate(params.value) : params.value
      }

      return typeof params == "string" ? parseDate(params) : params
    },
    renderEditCell: params => {
      return <DateTimeEditInputCell {...params} />
    },
  },
}

export const columnsUpload = [
  {
    field: "Company",
    headerName: "Company",
    width: 100,
    editable: false,
    maxLength: 5,
    required: true,
  },
  {
    field: "warehouse",
    headerName: "Destination Warehouse",
    width: 180,
    editable: true,
    maxLength: 40,
    required: true,
    shouldNotServerSideValidationFirstTime: true,
  },
  {
    field: "receipt_id",
    headerName: "Shipment ID",
    mappings: ["Receipt ID"],
    minWidth: 180,
    flex: 1,
    editable: true,
    maxLength: 25,
  },
  {
    field: "receipt_type",
    headerName: "Shipment Type",
    mappings: ["Receipt Type"],
    editable: true,
    maxLength: 40,
    required: true,
    width: 200,
  },
  {
    field: "item",
    headerName: "Product Code",
    editable: true,
    maxLength: 40,
    required: true,
    minWidth: 200,
    flex: 1,
  },
  {
    field: "quantity",
    headerName: "Qty",
    type: "number",
    editable: true,
    maxLength: 6,
    required: true,
    width: 100,
    minValue: 0,
  },
  {
    field: "scheduled_date",
    headerName: "Expected eStore Arrival Date",
    editable: true,
    required: true,
    width: 220,
  },
  {
    field: "receipt_date",
    headerName: "Confirmed eStore Unload Date",
    editable: true,
    width: 220,
    type: "date",
  },
]

export const reorderedKeys = ["receipt_date", "receipt_id", "receipt_type", "scheduled_date", "warehouse", "details"]

export const detailsKeys = ["action", "erp_order_line_num", "erp_order", "item", "quantity", "quantity_um"]
